import type { LogoInk } from '@ip/shared-types';

/**
 * A company logo framed on a plate.
 *
 * Company logos are transparent PNGs and come in both flavours — the FSS logo
 * is WHITE ink, others are near-black — so no single plate colour works for
 * all of them, and matching the plate to the THEME does not help either (a
 * white logo dies on a light theme, a black one on a dark theme). The plate is
 * therefore chosen from the logo's own ink tone, measured at upload:
 *
 *   light ink → dark plate      dark ink → white plate
 *
 * Both plates are fixed colours, so the logo reads identically in the light and
 * the dark theme; only the thin border adapts, to keep the chip from floating.
 * object-contain preserves the aspect ratio inside the rounded square.
 */
export function CompanyLogo({
  src,
  alt,
  ink,
  className = 'size-8',
}: {
  src: string;
  alt: string;
  ink?: LogoInk | null;
  className?: string;
}) {
  const darkPlate = ink === 'light';
  return (
    <div
      className={`flex aspect-square ${className} shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 ${
        darkPlate
          ? 'border-white/15 bg-neutral-800 dark:border-white/20'
          : 'border-black/10 bg-white dark:border-white/20'
      }`}
    >
      <img src={src} alt={alt} className="size-full object-contain" loading="lazy" />
    </div>
  );
}
